import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Chip,
  Avatar,
  Typography,
  IconButton,
  Divider,
  Alert,
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { setHours, setMinutes, addHours } from 'date-fns';
import { useFamily } from '../../context';
import { FamilySelector, useFamilySelection } from '../common';
import { colors } from '../../theme';
import type {
  Event,
  CalendarType,
  RepeatType,
  AlertType,
  ParticipantInput,
} from '../../types';

interface EventFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (event: Partial<Event>, participants?: ParticipantInput[]) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  event?: Event | null;
  initialDate?: Date;
}

const CALENDAR_OPTIONS: Array<{ key: CalendarType; label: string }> = [
  { key: 'General', label: 'General' },
  { key: 'Activities', label: 'Activities' },
  { key: 'Education', label: 'Education' },
  { key: 'Health', label: 'Health' },
  { key: 'Money', label: 'Money' },
  { key: 'Shopping', label: 'Shopping' },
  { key: 'Discipline', label: 'Discipline' },
  { key: 'Childcare', label: 'Childcare' },
];

const REPEAT_OPTS: Array<{ key: RepeatType; label: string }> = [
  { key: 'None', label: 'None' },
  { key: 'Daily', label: 'Daily' },
  { key: 'Weekly', label: 'Weekly' },
  { key: 'Bi-Weekly', label: 'Bi-Weekly' },
  { key: 'Monthly', label: 'Monthly' },
  { key: 'Yearly', label: 'Yearly' },
];

const ALERT_OPTS: Array<{ key: AlertType; label: string }> = [
  { key: 'None', label: 'None' },
  { key: '0', label: 'At time of event' },
  { key: '300', label: '5 minutes before' },
  { key: '900', label: '15 minutes before' },
  { key: '1800', label: '30 minutes before' },
  { key: '3600', label: '1 hour before' },
  { key: '7200', label: '2 hours before' },
  { key: '86400', label: '1 day before' },
  { key: '172800', label: '2 days before' },
  { key: '604800', label: '1 week before' },
];

const DAYS_OF_WEEK = [
  { key: 'MO', label: 'Mon' },
  { key: 'TU', label: 'Tue' },
  { key: 'WE', label: 'Wed' },
  { key: 'TH', label: 'Thu' },
  { key: 'FR', label: 'Fri' },
  { key: 'SA', label: 'Sat' },
  { key: 'SU', label: 'Sun' },
];

const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, i) => i + 1);

const getCalendarColor = (calendar: CalendarType): string => {
  const colorMap: Record<string, string> = {
    General: colors.calendar,
    Activities: colors.activities,
    Education: colors.school,
    Health: colors.health,
    Money: colors.money,
    Shopping: colors.calendar,
    Discipline: colors.discipline,
    Childcare: colors.calendar,
  };
  return colorMap[calendar] || colors.calendar;
};

export const EventFormDialog: React.FC<EventFormDialogProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  event,
  initialDate,
}) => {
  const { memberList, getMemberName, getMemberColor, currentFamilyId, personId } = useFamily();
  const isEditing = !!event;
  const {
    selectedFamilyIds,
    setSelectedFamilyIds,
    showSelector: showFamilySelector,
  } = useFamilySelection(event?.families && event.families.length > 0 ? event.families : undefined);

  // Filter members to show only those from selected families
  // Since memberList contains members from the current family, show all members
  // when the current family is one of the selected families
  const availableMembers = useMemo(() => {
    if (!currentFamilyId || selectedFamilyIds.length === 0) {
      return memberList;
    }
    // Show all members from memberList if current family is selected
    // (memberList only contains current family's members)
    if (selectedFamilyIds.includes(currentFamilyId)) {
      return memberList;
    }
    // If current family is not selected, we can't show members from other families
    // due to current architecture limitations - return empty for now
    return [];
  }, [memberList, selectedFamilyIds, currentFamilyId]);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [isAllDay, setIsAllDay] = useState(false);
  const [calendar, setCalendar] = useState<CalendarType>('General');
  const [repeat, setRepeat] = useState<RepeatType>('None');
  const [alert, setAlert] = useState<AlertType>('None');
  const [notes, setNotes] = useState('');
  const [url, setUrl] = useState('');
  const [repeatDaysOfWeek, setRepeatDaysOfWeek] = useState<string[]>([]);
  const [repeatDaysOfMonth, setRepeatDaysOfMonth] = useState<number[]>([]);
  const [repeatEndDate, setRepeatEndDate] = useState<Date | null>(null);
  const [selectedAttendees, setSelectedAttendees] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (event) {
        // Editing existing event
        setTitle(event.title || '');
        setDescription(event.description || '');
        setLocation(typeof event.location === 'string' ? event.location : event.location?.address || '');
        setStartDate(new Date(event.startTime));
        setStartTime(new Date(event.startTime));
        setEndDate(new Date(event.endTime));
        setEndTime(new Date(event.endTime));
        setIsAllDay(event.isAllDay || false);
        setCalendar(event.calendar || 'General');
        setRepeat(event.repeat || 'None');
        setAlert(event.alert || 'None');
        setNotes(event.notes || '');
        setUrl(event.url || '');
        setSelectedAttendees(Object.keys(event.attendees || {}));
        setRepeatDaysOfWeek((event as any).repeatDaysOfWeek || []);
        setRepeatDaysOfMonth((event as any).repeatDaysOfMonth || []);
        setRepeatEndDate((event as any).repeatEndDate ? new Date((event as any).repeatEndDate) : null);
      } else {
        // Creating new event
        const defaultDate = initialDate || new Date();
        const defaultStartTime = setMinutes(setHours(defaultDate, 9), 0);
        const defaultEndTime = addHours(defaultStartTime, 1);

        setTitle('');
        setDescription('');
        setLocation('');
        setStartDate(defaultDate);
        setStartTime(defaultStartTime);
        setEndDate(defaultDate);
        setEndTime(defaultEndTime);
        setIsAllDay(false);
        setCalendar('General');
        setRepeat('None');
        setAlert('None');
        setNotes('');
        setUrl('');
        setRepeatDaysOfWeek([]);
        setRepeatDaysOfMonth([]);
        setRepeatEndDate(null);
        // Default to current user as participant
        setSelectedAttendees(personId ? [personId] : []);
      }
      setError(null);
    }
  }, [open, event, initialDate, personId]);

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!startDate) {
      setError('Start date is required');
      return;
    }
    // Participants are required (at least one)
    if (selectedAttendees.length === 0) {
      setError('Please select at least one participant');
      return;
    }
    if ((repeat === 'Weekly' || repeat === 'Bi-Weekly') && repeatDaysOfWeek.length === 0) {
      setError('Please select at least one day of the week');
      return;
    }
    if (repeat === 'Monthly' && repeatDaysOfMonth.length === 0) {
      setError('Please select at least one day of the month');
      return;
    }
    // Family is now optional - events are participant-focused

    setSaving(true);
    setError(null);

    try {
      // Combine date and time
      let startDateTime = startDate;
      let endDateTime = endDate || startDate;

      if (!isAllDay && startTime) {
        startDateTime = new Date(startDate);
        startDateTime.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);
      }

      if (!isAllDay && endTime) {
        endDateTime = new Date(endDate || startDate);
        endDateTime.setHours(endTime.getHours(), endTime.getMinutes(), 0, 0);
      }

      if (isAllDay) {
        startDateTime.setHours(0, 0, 0, 0);
        endDateTime.setHours(23, 59, 59, 999);
      }

      const eventData: Partial<Event> = {
        title: title.trim(),
        description: description.trim() || undefined,
        location: location.trim() || undefined,
        startTime: startDateTime.getTime(),
        endTime: endDateTime.getTime(),
        isAllDay,
        calendar,
        repeat: repeat !== 'None' ? repeat : undefined,
        alert: alert !== 'None' ? alert : undefined,
        notes: notes.trim() || undefined,
        url: url.trim() || undefined,
        attendees: selectedAttendees.reduce((acc, id) => ({ ...acc, [id]: true }), {}),
        // Include selected families (for multi-family support)
        families: selectedFamilyIds,
        // Recurrence configuration
        repeatDaysOfWeek: (repeat === 'Weekly' || repeat === 'Bi-Weekly') && repeatDaysOfWeek.length > 0 ? repeatDaysOfWeek : undefined,
        repeatDaysOfMonth: repeat === 'Monthly' && repeatDaysOfMonth.length > 0 ? repeatDaysOfMonth : undefined,
        repeatEndDate: repeat !== 'None' && repeatEndDate ? repeatEndDate.getTime() : undefined,
      } as any;

      const participants: ParticipantInput[] = selectedAttendees.map((id) => ({
        person_id: id,
        status: 'invited',
        role: 'attendee',
      }));

      await onSave(eventData, participants);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!event?.id || !onDelete) return;

    setSaving(true);
    try {
      await onDelete(event.id);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete event');
    } finally {
      setSaving(false);
    }
  };

  const toggleAttendee = (personId: string) => {
    setSelectedAttendees((prev) =>
      prev.includes(personId)
        ? prev.filter((id) => id !== personId)
        : [...prev, personId]
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{isEditing ? 'Edit Event' : 'New Event'}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Title */}
          <Grid size={12}>
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              required
              autoFocus
            />
          </Grid>

          {/* Family Selector - only shows if user belongs to multiple families and not editing */}
          {showFamilySelector && !isEditing && (
            <Grid size={12}>
              <FamilySelector
                value={selectedFamilyIds}
                onChange={setSelectedFamilyIds}
              />
            </Grid>
          )}

          {/* Calendar Type */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Calendar</InputLabel>
              <Select
                value={calendar}
                label="Calendar"
                onChange={(e) => setCalendar(e.target.value as CalendarType)}
              >
                {CALENDAR_OPTIONS.map((opt) => (
                  <MenuItem key={opt.key} value={opt.key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: getCalendarColor(opt.key),
                        }}
                      />
                      {opt.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* All Day Toggle */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isAllDay}
                  onChange={(e) => setIsAllDay(e.target.checked)}
                />
              }
              label="All Day"
              sx={{ mt: 1 }}
            />
          </Grid>

          {/* Start Date/Time */}
          <Grid size={{ xs: 12, sm: isAllDay ? 6 : 3 }}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(date) => {
                setStartDate(date);
                if (!endDate || (date && date > endDate)) {
                  setEndDate(date);
                }
              }}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          {!isAllDay && (
            <Grid size={{ xs: 12, sm: 3 }}>
              <TimePicker
                label="Start Time"
                value={startTime}
                onChange={(time) => setStartTime(time)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
          )}

          {/* End Date/Time */}
          <Grid size={{ xs: 12, sm: isAllDay ? 6 : 3 }}>
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(date) => setEndDate(date)}
              minDate={startDate || undefined}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          {!isAllDay && (
            <Grid size={{ xs: 12, sm: 3 }}>
              <TimePicker
                label="End Time"
                value={endTime}
                onChange={(time) => setEndTime(time)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
          )}

          {/* Repeat */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Repeat</InputLabel>
              <Select
                value={repeat}
                label="Repeat"
                onChange={(e) => setRepeat(e.target.value as RepeatType)}
              >
                {REPEAT_OPTS.map((opt) => (
                  <MenuItem key={opt.key} value={opt.key}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Recurrence Day-of-Week selector (Weekly/Bi-Weekly) */}
          {(repeat === 'Weekly' || repeat === 'Bi-Weekly') && (
            <Grid size={12}>
              <Typography variant="subtitle2" gutterBottom>Repeat On</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {DAYS_OF_WEEK.map((day) => (
                  <Chip
                    key={day.key}
                    label={day.label}
                    onClick={() => setRepeatDaysOfWeek((prev) =>
                      prev.includes(day.key) ? prev.filter((d) => d !== day.key) : [...prev, day.key]
                    )}
                    variant={repeatDaysOfWeek.includes(day.key) ? 'filled' : 'outlined'}
                    color={repeatDaysOfWeek.includes(day.key) ? 'primary' : 'default'}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Grid>
          )}

          {/* Recurrence Day-of-Month selector (Monthly) */}
          {repeat === 'Monthly' && (
            <Grid size={12}>
              <Typography variant="subtitle2" gutterBottom>Repeat On Days</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {DAYS_OF_MONTH.map((day) => (
                  <Chip
                    key={day}
                    label={day}
                    size="small"
                    onClick={() => setRepeatDaysOfMonth((prev) =>
                      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
                    )}
                    variant={repeatDaysOfMonth.includes(day) ? 'filled' : 'outlined'}
                    color={repeatDaysOfMonth.includes(day) ? 'primary' : 'default'}
                    sx={{ cursor: 'pointer', minWidth: 36 }}
                  />
                ))}
              </Box>
            </Grid>
          )}

          {/* Repeat End Date */}
          {repeat !== 'None' && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <DatePicker
                label="Repeat Until"
                value={repeatEndDate}
                onChange={(date) => setRepeatEndDate(date)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
          )}

          {/* Alert */}
          <Grid size={{ xs: 12, sm: repeat !== 'None' ? 6 : 6 }}>
            <FormControl fullWidth>
              <InputLabel>Alert</InputLabel>
              <Select
                value={alert}
                label="Alert"
                onChange={(e) => setAlert(e.target.value as AlertType)}
              >
                {ALERT_OPTS.map((opt) => (
                  <MenuItem key={opt.key} value={opt.key}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Location */}
          <Grid size={12}>
            <TextField
              label="Location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              fullWidth
              placeholder="Add location"
            />
          </Grid>

          {/* Description */}
          <Grid size={12}>
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="Add description"
            />
          </Grid>

          {/* Attendees */}
          <Grid size={12}>
            <Typography variant="subtitle2" gutterBottom>
              Attendees
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {availableMembers.map((member) => {
                const isSelected = selectedAttendees.includes(member.id);
                const name = getMemberName(member.id);
                const color = getMemberColor(member.id);

                return (
                  <Chip
                    key={member.id}
                    avatar={
                      <Avatar sx={{ bgcolor: color }}>
                        {name.charAt(0).toUpperCase()}
                      </Avatar>
                    }
                    label={name}
                    onClick={() => toggleAttendee(member.id)}
                    variant={isSelected ? 'filled' : 'outlined'}
                    color={isSelected ? 'primary' : 'default'}
                    sx={{ cursor: 'pointer' }}
                  />
                );
              })}
            </Box>
          </Grid>

          <Grid size={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          {/* Notes */}
          <Grid size={12}>
            <TextField
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Add notes"
            />
          </Grid>

          {/* URL */}
          <Grid size={12}>
            <TextField
              label="URL"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              fullWidth
              placeholder="Add link"
              type="url"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {isEditing && onDelete && (
          <Button
            onClick={handleDelete}
            color="error"
            startIcon={<DeleteIcon />}
            disabled={saving}
            sx={{ mr: 'auto' }}
          >
            Delete
          </Button>
        )}
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventFormDialog;
